import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { schedulesService } from "../services/firebase/schedules.service";
import {
  Schedule,
  ScheduleFormData,
  WorkingHours,
} from "../types/schedule.types";

/**
 * Custom hook for managing schedules
 * Provides CRUD operations with React Query for caching and optimistic updates
 */
export function useSchedules(
  shopId?: string,
  month?: string,
  dateRange?: { startDate: string; endDate: string }
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = user?.organizationId;

  // Build query key based on parameters
  const queryKey = dateRange
    ? [
        "schedules",
        organizationId,
        shopId,
        dateRange.startDate,
        dateRange.endDate,
      ]
    : month
    ? ["schedules", organizationId, shopId, month]
    : ["schedules", organizationId, shopId];

  // Fetch schedules by shop and month or date range
  const {
    data: schedules = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!organizationId) return [];

      // If date range provided, fetch by date range
      if (dateRange) {
        const schedules = await schedulesService.getSchedulesByDateRange(
          organizationId,
          dateRange.startDate,
          dateRange.endDate
        );
        return schedules;
      }

      // If no shopId provided, fetch all schedules for the month
      if (!shopId && month) {
        const schedules = await schedulesService.getSchedulesByMonth(
          organizationId,
          month
        );
        return schedules;
      }

      // If shopId provided, fetch schedules for specific shop
      if (shopId && month) {
        return schedulesService.getSchedulesByShopAndMonth(
          organizationId,
          shopId,
          month
        );
      }

      return [];
    },
    enabled: !!organizationId && (!!shopId || !!month || !!dateRange),
  });

  // Create schedule mutation
  const createScheduleMutation = useMutation({
    mutationFn: (data: ScheduleFormData) => {
      if (!organizationId) throw new Error("No organization ID");
      return schedulesService.createSchedule(organizationId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["schedules", organizationId],
      });
    },
  });

  // Update schedule mutation
  const updateScheduleMutation = useMutation({
    mutationFn: ({
      scheduleId,
      data,
    }: {
      scheduleId: string;
      data: Partial<ScheduleFormData>;
    }) => {
      if (!organizationId) throw new Error("No organization ID");
      return schedulesService.updateSchedule(organizationId, scheduleId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["schedules", organizationId],
      });
    },
  });

  // Delete schedule mutation
  const deleteScheduleMutation = useMutation({
    mutationFn: (scheduleId: string) => {
      if (!organizationId) throw new Error("No organization ID");
      return schedulesService.deleteSchedule(organizationId, scheduleId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["schedules", organizationId],
      });
    },
  });

  // Add or update employee working hours mutation
  const addOrUpdateEmployeeWorkingHoursMutation = useMutation({
    mutationFn: ({
      scheduleId,
      employeeId,
      employeeName,
      workingHours,
    }: {
      scheduleId: string;
      employeeId: string;
      employeeName: string;
      workingHours: WorkingHours;
    }) => {
      if (!organizationId) throw new Error("No organization ID");
      return schedulesService.addOrUpdateEmployeeWorkingHours(
        organizationId,
        scheduleId,
        employeeId,
        employeeName,
        workingHours
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["schedules", organizationId],
      });
    },
  });

  // Remove employee working hours mutation
  const removeEmployeeWorkingHoursMutation = useMutation({
    mutationFn: ({
      scheduleId,
      employeeId,
      workingHoursId,
    }: {
      scheduleId: string;
      employeeId: string;
      workingHoursId: string;
    }) => {
      if (!organizationId) throw new Error("No organization ID");
      return schedulesService.removeEmployeeWorkingHours(
        organizationId,
        scheduleId,
        employeeId,
        workingHoursId
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["schedules", organizationId],
      });
    },
  });

  // Bulk update employee working hours mutation
  const bulkUpdateEmployeeWorkingHoursMutation = useMutation({
    mutationFn: ({
      scheduleId,
      changes,
    }: {
      scheduleId: string;
      changes: Array<{
        employeeId: string;
        employeeName: string;
        workingHours: WorkingHours[];
      }>;
    }) => {
      if (!organizationId) throw new Error("No organization ID");
      return schedulesService.bulkUpdateEmployeeWorkingHours(
        organizationId,
        scheduleId,
        changes
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["schedules", organizationId],
      });
    },
  });

  // Helper functions
  const createSchedule = async (data: ScheduleFormData): Promise<string> => {
    return createScheduleMutation.mutateAsync(data);
  };

  const updateSchedule = async (
    scheduleId: string,
    data: Partial<ScheduleFormData>
  ): Promise<void> => {
    return updateScheduleMutation.mutateAsync({ scheduleId, data });
  };

  const deleteSchedule = async (scheduleId: string): Promise<void> => {
    return deleteScheduleMutation.mutateAsync(scheduleId);
  };

  const addOrUpdateEmployeeWorkingHours = async (
    scheduleId: string,
    employeeId: string,
    employeeName: string,
    workingHours: WorkingHours
  ): Promise<void> => {
    return addOrUpdateEmployeeWorkingHoursMutation.mutateAsync({
      scheduleId,
      employeeId,
      employeeName,
      workingHours,
    });
  };

  const removeEmployeeWorkingHours = async (
    scheduleId: string,
    employeeId: string,
    workingHoursId: string
  ): Promise<void> => {
    return removeEmployeeWorkingHoursMutation.mutateAsync({
      scheduleId,
      employeeId,
      workingHoursId,
    });
  };

  const bulkUpdateEmployeeWorkingHours = async (
    scheduleId: string,
    changes: Array<{
      employeeId: string;
      employeeName: string;
      workingHours: WorkingHours[];
    }>
  ): Promise<void> => {
    return bulkUpdateEmployeeWorkingHoursMutation.mutateAsync({
      scheduleId,
      changes,
    });
  };

  const getScheduleForDate = (
    date: string,
    shopId: string
  ): Schedule | undefined => {
    return schedules.find(
      (schedule) => schedule.date === date && schedule.shopId === shopId
    );
  };

  return {
    // Data
    schedules,
    isLoading,
    error,

    // Functions
    createSchedule,
    updateSchedule,
    deleteSchedule,
    addOrUpdateEmployeeWorkingHours,
    removeEmployeeWorkingHours,
    bulkUpdateEmployeeWorkingHours,
    getScheduleForDate,
    refetch,

    // Mutation states
    isCreating: createScheduleMutation.isPending,
    isUpdating: updateScheduleMutation.isPending,
    isDeleting: deleteScheduleMutation.isPending,
    isAddingOrUpdating: addOrUpdateEmployeeWorkingHoursMutation.isPending,
    isRemoving: removeEmployeeWorkingHoursMutation.isPending,
    isBulkUpdating: bulkUpdateEmployeeWorkingHoursMutation.isPending,
  };
}
